import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import {
  checkUserHasManagerRole,
  readSessionCurrentManager,
} from "app/services/app.state.service";
import {
  loadMembersForManagerCached,
  loadScopeManagersForIds,
} from "app/services/manager.service";
import { TeamMemberDataGridBase } from "app/components/team-members/TeamMemberDataGridBase";
import { ClientFilterSelect } from "app/components/common/ClientFilterSelect";
import { ALL_CLIENTS } from "AppConstants";
import { mergeManagerScope } from "utils/managerSubtree";
import { useManagerScopeOptions } from "app/hooks/useManagerScopeOptions";
import { toManagerGridRows } from "utils/managerRows";

// Stable identity for a cold cache, so the selector doesn't hand back a new object each render.
const EMPTY_CACHE = {};

export const TeamMembersForManager = ({ clickEventHandler }) => {
  const dispatch = useDispatch();
  const membersByManager = useSelector((state) => state.client?.membersByManager || EMPTY_CACHE);
  const [members, setMembers] = useState([]);
  const [teamClientId, setTeamClientId] = useState(ALL_CLIENTS);
  const [managerClients, setManagerClients] = useState([]);
  const [subtreeManagers, setSubtreeManagers] = useState([]);
  const [isScopeResolved, setIsScopeResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // A team list that lost a request is wrong, not short, and an empty grid looks the same
  // as a correct one — so a swallowed failure is said out loud rather than rendered away.
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    const list = readSessionCurrentManager();
    setManagerClients(Array.isArray(list) ? list : []);
  }, []);

  // Roll the subtree up from every record the manager holds, not just the first: an L1
  // child may report through any of them, and their members belong in this list too.
  useEffect(() => {
    if (!checkUserHasManagerRole()) return undefined;
    const managerIds = managerClients.map((c) => c.id).filter((id) => id != null);
    if (managerIds.length === 0) return undefined;
    let cancelled = false;
    loadScopeManagersForIds(managerIds, () => {
      if (!cancelled) setHasLoadError(true);
    })
      .then((list) => {
        if (!cancelled) setSubtreeManagers(list);
      })
      .catch(() => {
        if (!cancelled) setSubtreeManagers([]);
      })
      .finally(() => {
        // Resolved either way: a failed roll-up still leaves the manager's own records to load.
        if (!cancelled) setIsScopeResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [managerClients]);

  const { clientOptions, subtreeL1Options } = useManagerScopeOptions({
    subtreeManagers,
    managerClients,
    selectedClient: teamClientId,
  });

  // The managers reporting to the logged-in manager, shown alongside their members so an L2
  // sees their L1s as well as the people under them.
  const l1ManagerRows = useMemo(() => toManagerGridRows(subtreeL1Options), [subtreeL1Options]);

  const rows = useMemo(() => [...l1ManagerRows, ...members], [l1ManagerRows, members]);

  // Members come from the manager-scoped endpoint, so the grid lists only the people
  // reporting to a manager in scope rather than everyone on the client (HRMS-314).
  useEffect(() => {
    if (!checkUserHasManagerRole()) return undefined;
    // Wait for the subtree so the members load in one pass, not once per scope source.
    if (!isScopeResolved) return undefined;
    const scope = mergeManagerScope(subtreeManagers, managerClients);
    const managersInScope =
      teamClientId === ALL_CLIENTS
        ? scope
        : scope.filter((m) => String(m.clientId) === String(teamClientId));
    if (managersInScope.length === 0) {
      setMembers([]);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setHasLoadError(false);
    Promise.all(
      managersInScope.map((m) =>
        loadMembersForManagerCached(m.id, membersByManager, dispatch, () => {
          if (!cancelled) setHasLoadError(true);
        })
      )
    ).then((lists) => {
      if (cancelled) return;
      const seen = new Set();
      const merged = lists.flat().filter((m) => (seen.has(m.id) ? false : seen.add(m.id)));
      // A reporting row pointing at another client's manager would otherwise leak that
      // client's member into a filtered list, so match the chosen client on the row too.
      setMembers(
        teamClientId === ALL_CLIENTS
          ? merged
          : merged.filter((m) => String(m.client?.id ?? m.clientId) === String(teamClientId))
      );
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // membersByManager is read but omitted from deps: this view writes to it, so depending
    // on it would self-trigger a re-run (client and scope changes suffice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamClientId, subtreeManagers, managerClients, isScopeResolved]);

  if (!checkUserHasManagerRole()) {
    return <div>You do not have access to this functionality.</div>;
  }

  return (
    <Card>
      <MDBox
        mx={2}
        mt={-3}
        py={1}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          Client Team Members
        </MDTypography>
      </MDBox>

      {hasLoadError && (
        <MDBox px={2} pt={2}>
          <MDTypography variant="button" color="error">
            Some of this team could not be loaded, so the list below may be incomplete. Please
            refresh to try again.
          </MDTypography>
        </MDBox>
      )}

      <MDBox px={2} pt={3}>
        <ClientFilterSelect
          value={teamClientId}
          onChange={setTeamClientId}
          options={clientOptions}
          id="team-members-client"
        />
      </MDBox>

      <TeamMemberDataGridBase
        membersData={rows}
        isLoading={isLoading}
        clickEventHandler={clickEventHandler}
      />
    </Card>
  );
};
